// Text pages that admins can edit in the browser (Markdown): "Credits & sources" and "How to use the atlas".
// Credits also lists every basemap's attribution and the geology model's sources, gathered by the server so a
// newly added layer is credited automatically.
import { useState, useEffect, useRef } from 'preact/hooks';
import { html, Modal, notify, md } from './common.js';
import { get, api } from '../api.js';
import { me, isAdmin } from '../store.js';

function EditablePage({ title, url, label, hint, children, toc }) {
  const [c, setC] = useState(null), [edit, setEdit] = useState(null), [busy, setBusy] = useState(false);
  const [heads, setHeads] = useState([]);
  const body = useRef();
  useEffect(() => { get(url).then(setC).catch((e) => notify(e.message, 'err')); }, [url]);
  useEffect(() => {                         // table of contents from the ## headings
    if (!toc || !body.current) { setHeads([]); return; }
    setHeads([...body.current.querySelectorAll('h2')].map((h) => ({ h, t: h.textContent })));
  }, [c, edit, toc]);
  const save = async (markdown) => {
    setBusy(true);
    try { setC(await api(url, { method: 'PUT', body: { markdown } })); setEdit(null); notify(`${label} saved`); }
    catch (e) { notify(e.message, 'err'); }
    setBusy(false);
  };
  const admin = isAdmin();
  const footer = edit != null ? html`
      <button class="btn" onClick=${() => setEdit(null)}>Cancel</button>
      ${c?.customised && html`<button class="btn" disabled=${busy} onClick=${() => save(null)}>Restore the original text</button>`}
      <button class="btn primary" disabled=${busy} onClick=${() => save(edit)}>Save</button>`
    : admin && c ? html`<button class="btn" onClick=${() => setEdit(c.source ?? c.markdown)}>Edit</button>` : null;
  return html`<${Modal} wide title=${title} footer=${footer}>
    ${!c ? html`<div class="muted">Loading…</div>` : edit != null ? html`
      <div class="small muted" style="margin-bottom:6px">Markdown: <code>## Heading</code>, <code>- list item</code>, <code>**bold**</code>,
        <code>*italic*</code>, <code>[link text](https://…)</code>. ${hint}</div>
      <textarea style="width:100%;min-height:420px;font-family:ui-monospace,monospace;font-size:12.5px" value=${edit}
        onInput=${(e) => setEdit(e.target.value)} />`
    : html`
      ${heads.length > 2 && html`<nav class="toc" aria-label="Contents">${heads.map(x => html`
        <a href="#" onClick=${(e) => { e.preventDefault(); x.h.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>${x.t}</a>`)}</nav>`}
      <div class="md page" ref=${body} dangerouslySetInnerHTML=${md(c.markdown)} />
      ${children?.(c)}
      ${c.customised && c.updated_by && html`<div class="tiny muted" style="margin-top:10px">Last edited by ${c.updated_by}${c.updated_at ? ', ' + c.updated_at.slice(0, 10) : ''}.</div>`}`}
  <//>`;
}

export function CreditsModal() {
  return html`<${EditablePage} title="Credits & sources" url="/api/credits" label="Credits"
    hint="The map and geology lists below the text are added automatically.">${(c) => html`
    ${c.basemaps?.length > 0 && html`<h3 style="font-size:14px;margin:14px 0 4px">Map layers in this atlas</h3>
      <ul class="small">${c.basemaps.map(b => html`<li><b>${b.label}</b>: ${b.attribution}</li>`)}</ul>`}
    ${c.geology?.sources?.length > 0 && html`<h3 style="font-size:14px;margin:14px 0 4px">Sources of the 3D geology model</h3>
      <ul class="small">${c.geology.sources.map(s => html`<li>${s}</li>`)}</ul>`}`}<//>`;
}

export function HowtoModal() {
  return html`<${EditablePage} toc title="How to use the atlas" url="/api/howto" label="How-to page"
    hint=${html`Lines between <code>${'<!-- for: contributors -->'}</code> and <code>${'<!-- end -->'}</code> are shown only to
      contributors and admins; <code>${'<!-- for: admins -->'}</code> only to admins.`} />`;
}
